/**
 * Message List Component - Redesigned with glass morphism
 * Displays chat messages with streaming support and dot pattern overlays
 * Validates: Requirements 13.1, 13.4, 20.4
 */
import { useEffect, useRef } from 'react';
import { ChatMessage } from '../../types';
import { MarkdownRenderer } from './MarkdownRenderer';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface MessageListProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingContent: string;
}

export function MessageList({ messages, isStreaming, streamingContent }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: `1px solid ${THEME.border}`,
            }}
          >
            <Icons.Message size={32} style={{ color: THEME.textMuted }} />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Start a conversation</h2>
          <p style={{ color: THEME.textSecondary }}>
            Select a model and send a message to begin chatting with AI.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      <div className="max-w-3xl mx-auto space-y-7">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {/* Streaming message */}
        {isStreaming && (
          <div className="animate-slide-up">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div
                  className="rounded-2xl p-5 border relative overflow-hidden"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    borderColor: THEME.border,
                  }}
                >
                  {/* Dot pattern overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
                      backgroundSize: '12px 12px',
                    }}
                  />

                  <div className="relative">
                    {streamingContent ? (
                      <MarkdownRenderer content={streamingContent} />
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full animate-blink"
                          style={{ backgroundColor: THEME.accentColor }}
                        />
                        <span
                          className="w-2 h-2 rounded-full animate-blink"
                          style={{
                            backgroundColor: THEME.accentColor,
                            animationDelay: '0.2s',
                          }}
                        />
                        <span
                          className="w-2 h-2 rounded-full animate-blink"
                          style={{
                            backgroundColor: THEME.accentColor,
                            animationDelay: '0.4s',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <div
          className="px-4 py-2 rounded-xl text-sm max-w-2xl"
          style={{
            backgroundColor: 'rgba(255,255,255,0.02)',
            color: THEME.textSecondary,
          }}
        >
          <MarkdownRenderer content={message.content} />
        </div>
      </div>
    );
  }

  return (
    <div className={`animate-slide-up ${isUser ? 'flex justify-end' : 'flex justify-start'}`}>
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        {/* Message bubble */}
        <div
          className="rounded-2xl p-5 border relative overflow-hidden"
          style={{
            backgroundColor: isUser ? THEME.accentSoft : 'rgba(255,255,255,0.03)',
            borderColor: isUser ? THEME.accentBorder : THEME.border,
          }}
        >
          {/* Dot pattern overlay for assistant messages */}
          {!isUser && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
                backgroundSize: '12px 12px',
              }}
            />
          )}

          {/* Content */}
          <div className="relative">
            <div
              style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: THEME.textMain,
              }}
            >
              <MarkdownRenderer content={message.content} />
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div
          className="flex items-center gap-3 mt-2 px-1 text-xs uppercase"
          style={{ color: THEME.textMuted, letterSpacing: '0.5px' }}
        >
          <span>{formatTimestamp(message.timestamp)}</span>
          {!isUser && (
            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-1 hover:text-white/70 transition-colors"
                title="Copy message"
              >
                <Icons.Copy size={12} />
                <span>Copy</span>
              </button>
              <button
                className="flex items-center gap-1 hover:text-white/70 transition-colors"
                title="Regenerate response"
              >
                <Icons.Refresh size={12} />
                <span>Regenerate</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimestamp(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}
